<template>
  <div style="display:flex;min-height:100vh;">
    <Sidebar />

    <main class="main">
      <Topbar />

      <div class="page-content">

        <!-- Loading -->
        <div v-if="loading" class="card card-pad" style="text-align:center;padding:60px;">
          <div class="spinner"></div>
          <p style="margin-top:12px;color:#9896bb;font-size:14px;">Loading finance data...</p>
        </div>

        <template v-else>

          <!-- XERO CONNECTION BANNER -->
          <div v-if="isConnected" class="xero-connected">
            <div class="xero-icon"><svg viewBox="0 0 16 16"><path d="M8 2a6 6 0 100 12A6 6 0 008 2z" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M5 8h6M8 5v6" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></div>
            <div class="xero-info">
              <div class="xero-title">Connected to Xero</div>
              <div class="xero-sub">{{ connectionData.tenant_name || 'Organization' }} · Last synced {{ formatDate(connectionData.connected_at) }}</div>
            </div>
            <button class="btn btn-outline btn-sm" @click="disconnectXero" :disabled="disconnecting">{{ disconnecting ? 'Disconnecting...' : 'Disconnect' }}</button>
          </div>

          <!-- NOT CONNECTED -->
          <div v-else class="card card-pad" style="margin-bottom:20px;text-align:center;padding:40px;">
            <div style="font-size:15px;font-weight:600;margin-bottom:8px;color:#1a1a2e;">Xero not connected</div>
            <div style="font-size:13px;color:#9896bb;margin-bottom:16px;">Connect to Xero to push invoices automatically.</div>
            <button class="btn btn-primary" @click="connectToXero" :disabled="connecting">{{ connecting ? 'Connecting...' : 'Connect to Xero' }}</button>
          </div>

          <!-- READY TO PUSH -->
          <div v-if="readyToPush.length > 0" style="margin-bottom:20px;">
            <div class="section-label">Ready to push</div>

            <div v-for="entry in readyToPush" :key="entry.month_label" class="push-card" :style="entry.can_push_to_xero ? '' : 'opacity:0.75'">
              <div class="push-card-icon" :style="entry.can_push_to_xero ? '' : 'background:#f0eef8'">
                <svg viewBox="0 0 16 16" :style="entry.can_push_to_xero ? 'stroke:#5b4fcf' : 'stroke:#9896bb'"><path d="M8 2v9M4 7l4 4 4-4"/><path d="M2 13h12"/></svg>
              </div>
              <div class="push-card-info">
                <div class="push-card-title" :style="entry.can_push_to_xero ? '' : 'color:#9896bb'">{{ entry.month_label }}</div>
                <div class="push-card-sub">
                  {{ entry.total_reports }} reports ·
                  <span v-if="entry.not_sent_reports > 0" style="color:#b7791f;font-weight:600">{{ entry.not_sent_reports }} still to send</span>
                  <span v-else>all emailed to owners</span>
                  · commission {{ formatCurrency(entry.commission_ex_vat) }} ex VAT
                </div>
              </div>
              <div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px;">
                <button v-if="entry.can_push_to_xero" class="btn btn-xero btn-sm" @click="openXeroConfirmModal(entry)" :disabled="pushing">Push to Xero</button>
                <button v-else class="btn btn-sm" style="background:#e8e6f4;color:#9896bb;cursor:not-allowed;" disabled>Push to Xero</button>
                <div v-if="!entry.can_push_to_xero" style="font-size:12px;color:#b7791f;">All reports must be sent first</div>
              </div>
            </div>
          </div>

          <!-- ALREADY PUSHED -->
          <div v-if="alreadyPushed.length > 0">
            <div class="section-label" style="margin-top:20px;">Already pushed</div>
            <div class="card">
              <div class="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Reports</th>
                      <th>Commission ex VAT</th>
                      <th>VAT</th>
                      <th>Pushed to Xero</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="entry in alreadyPushed" :key="entry.month_label">
                      <td><div class="prop-name">{{ entry.month_label }}</div></td>
                      <td style="color:#4a4880">{{ entry.total_reports }} reports</td>
                      <td><div class="amount">{{ formatCurrency(entry.commission_ex_vat) }}</div></td>
                      <td><div class="amount">{{ formatCurrency(entry.vat) }}</div></td>
                      <td>
                        <div style="display:flex;align-items:center;gap:7px;">
                          <div class="check-circle"><svg viewBox="0 0 12 12"><path d="M2 6l3 3 5-5"/></svg></div>
                          <div>
                            <div style="font-size:13px;font-weight:500;color:#00b894">Pushed</div>
                            <div style="font-size:11px;color:#9896bb">{{ formatDateTime(entry.pushed_at) }}</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- SEND TO BOOKKEEPER -->
          <div style="margin-top:24px;">
            <div class="section-label">Send to bookkeeper / accountant</div>
            <div class="card card-pad">
              <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
                <div>
                  <div style="font-size:14px;font-weight:600;margin-bottom:4px;">Send a financial summary by email</div>
                  <div style="font-size:13px;color:#9896bb;">Select one or more months to include.</div>
                </div>
                <button class="btn btn-ghost btn-sm" @click="showBkPanel = !showBkPanel" style="flex-shrink:0;">
                  Send to bookkeeper
                </button>
              </div>

              <!-- Bookkeeper panel -->
              <div v-if="showBkPanel" style="margin-top:16px;padding-top:16px;border-top:1px solid #e8e6f4;">
                <div class="section-label" style="margin-bottom:10px;">Select months to include</div>
                <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:16px;">
                  <div
                    v-for="entry in allMonths"
                    :key="entry.month_label"
                    :class="['bk-month-card', { selected: selectedBkMonths.includes(entry.month_label) }]"
                    @click="toggleBkMonth(entry.month_label)"
                  >
                    <div style="font-size:13px;font-weight:600;">{{ entry.month_label }}</div>
                    <div style="font-size:12px;color:#9896bb;margin-top:2px;">{{ entry.total_reports }} reports · {{ formatCurrency(entry.commission_ex_vat) }} ex VAT</div>
                    <div v-if="selectedBkMonths.includes(entry.month_label)" class="bk-tick">✓</div>
                  </div>
                </div>

                <div class="email-row"><label>Bookkeeper</label><input class="email-input" type="email" v-model="bkEmail" placeholder="bookkeeper@email.com"/></div>
                <div class="email-row"><label>Also send to</label><input class="email-input" type="email" v-model="bkEmailCc" placeholder="e.g. accountant, director…"/></div>

                <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:12px;">
                  <button class="btn btn-ghost btn-sm" @click="showBkPanel = false">Cancel</button>
                  <button class="btn btn-primary btn-sm" @click="sendToBookkeeper" :disabled="sendingBk || selectedBkMonths.length === 0">{{ sendingBk ? 'Sending...' : 'Send summary' }}</button>
                </div>
                <div style="font-size:12px;color:#9896bb;margin-top:6px;">A single PDF covering all selected months will be sent.</div>
              </div>
            </div>
          </div>

        </template>

      </div>
    </main>

    <!-- Modal Push to Xero Confirmation -->
    <div v-if="showXeroConfirmModal" class="modal-overlay" @click.self="closeXeroConfirmModal">
      <div class="modal-send-report">
        <div class="modal-header-gradient">
          <div class="modal-icon-circle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <h3 class="modal-title">Push to Xero</h3>
          <p class="modal-subtitle">{{ selectedEntry?.sent_reports }} invoice{{ selectedEntry?.sent_reports > 1 ? 's' : '' }} · {{ selectedEntry?.month_label }}</p>
        </div>

        <div class="modal-body">
          <div class="warning-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <span>{{ selectedEntry?.sent_reports }} sent report{{ selectedEntry?.sent_reports > 1 ? 's' : '' }} will be pushed to Xero as DRAFT invoices for review.</span>
          </div>

          <div class="xero-test-mode-row">
            <div class="xero-test-mode-info">
              <div class="xero-test-mode-title">Test mode</div>
              <p class="xero-test-mode-help">When ON, no data is sent to Xero — you'll see exactly what would be pushed.</p>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" v-model="xeroTestMode" :disabled="previewLoading || pushing" />
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeXeroConfirmModal" class="btn-modal-cancel" :disabled="pushing || previewLoading">Cancel</button>
          <button @click="confirmXeroAction" class="btn-modal-send" :disabled="pushing || previewLoading">
            <span v-if="previewLoading">Loading preview...</span>
            <span v-else-if="pushing">Pushing...</span>
            <span v-else-if="xeroTestMode">Show preview</span>
            <span v-else>Push to Xero</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Xero Test Preview -->
    <div v-if="showXeroPreviewModal" class="modal-overlay" @click.self="closeXeroPreviewModal">
      <div class="modal-xero-preview">
        <div class="modal-header-gradient">
          <div class="modal-icon-circle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <h3 class="modal-title">Xero Test Preview</h3>
          <p class="modal-subtitle">{{ xeroPreviewData?.count }} invoice{{ xeroPreviewData?.count > 1 ? 's' : '' }} — no data sent to Xero</p>
        </div>

        <div class="modal-body modal-body-scroll">
          <div
            v-for="(inv, idx) in xeroPreviewData?.invoices"
            :key="idx"
            class="xero-preview-invoice"
          >
            <div class="xero-preview-invoice-header">
              <div class="xero-preview-invoice-title">{{ inv.property_name }}</div>
              <div class="xero-preview-invoice-period">{{ inv.period }}</div>
            </div>

            <div class="xero-preview-meta-grid">
              <div class="xero-preview-meta-row">
                <span class="xero-preview-meta-label">Contact:</span>
                <span class="xero-preview-meta-value">
                  {{ inv.contact.name }}
                  <span v-if="inv.contact.email" class="xero-preview-meta-sub">&lt;{{ inv.contact.email }}&gt;</span>
                  <span v-if="!inv.contact.existing_in_xero" class="xero-preview-badge-new">will be created</span>
                </span>
              </div>
              <div class="xero-preview-meta-row">
                <span class="xero-preview-meta-label">Reference:</span>
                <span class="xero-preview-meta-value">{{ inv.reference }}</span>
              </div>
              <div class="xero-preview-meta-row">
                <span class="xero-preview-meta-label">Date / Due:</span>
                <span class="xero-preview-meta-value">{{ inv.date }} → {{ inv.due_date }}</span>
              </div>
              <div class="xero-preview-meta-row">
                <span class="xero-preview-meta-label">Currency:</span>
                <span class="xero-preview-meta-value">{{ inv.currency }}</span>
              </div>
              <div class="xero-preview-meta-row" v-if="inv.amounts_tax_exclusive">
                <span class="xero-preview-meta-label">Amounts are:</span>
                <span class="xero-preview-meta-value">Tax exclusive</span>
              </div>
            </div>

            <table class="xero-preview-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th class="num">Qty</th>
                  <th class="num">Price</th>
                  <th>Account</th>
                  <th>Tax rate</th>
                  <th class="num">Tax amount</th>
                  <th class="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line, lidx) in inv.line_items" :key="lidx" :class="{ 'xero-preview-row-novat': line.tax_amount === 0 }">
                  <td>{{ line.description }}</td>
                  <td class="num">{{ line.quantity }}</td>
                  <td class="num">{{ line.unit_price.toFixed(2) }}</td>
                  <td>{{ line.account_code }} - {{ line.account_name }}</td>
                  <td>
                    <span v-if="line.tax_amount === 0" class="xero-preview-novat-badge">{{ line.tax_rate_label }}</span>
                    <span v-else>{{ line.tax_rate_label }}</span>
                  </td>
                  <td class="num">{{ line.tax_amount.toFixed(2) }}</td>
                  <td class="num">{{ line.line_total.toFixed(2) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="5" class="xero-preview-total-label">Subtotal</td>
                  <td></td>
                  <td class="num">{{ inv.totals.subtotal.toFixed(2) }}</td>
                </tr>
                <tr>
                  <td colspan="5" class="xero-preview-total-label">Total VAT</td>
                  <td></td>
                  <td class="num">{{ inv.totals.total_tax.toFixed(2) }}</td>
                </tr>
                <tr class="xero-preview-grand-total">
                  <td colspan="5" class="xero-preview-total-label">Total</td>
                  <td></td>
                  <td class="num">{{ inv.totals.total.toFixed(2) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeXeroPreviewModal" class="btn-modal-ok">Close</button>
        </div>
      </div>
    </div>

    <!-- Modal Success Xero -->
    <div v-if="showSuccessModal" class="modal-overlay" @click.self="showSuccessModal = false">
      <div class="modal-success">
        <div class="modal-header-success">
          <div class="checkmark-circle">
            <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
              <circle class="checkmark-circle-bg" cx="26" cy="26" r="25" fill="none"/>
              <path class="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
            </svg>
          </div>
          <h3 class="modal-title-success">{{ successMessage }}</h3>
        </div>

        <div class="modal-body-success">
          <div v-if="successDetails.length > 0" class="success-details">
            <div
              v-for="(detail, index) in successDetails"
              :key="index"
              :class="['detail-item', detail.type === 'error' ? 'detail-error' : 'detail-success']"
            >
              <span v-if="detail.type === 'success'" class="detail-icon">✓</span>
              <span v-else class="detail-icon">✗</span>
              <span class="detail-text">{{ detail.text }}</span>
            </div>
          </div>
        </div>

        <div class="modal-footer-success">
          <button @click="showSuccessModal = false" class="btn-modal-ok">OK</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';
import Topbar from '@/components/Topbar.vue';

export default {
  name: 'XeroSettingsView',
  components: { Sidebar, Topbar },
  data() {
    return {
      loading: true,
      isConnected: false,
      connecting: false,
      disconnecting: false,
      pushing: false,
      connectionData: {},
      readyToPush: [],
      alreadyPushed: [],
      showBkPanel: false,
      selectedBkMonths: [],
      bkEmail: '',
      bkEmailCc: '',
      sendingBk: false,
      showXeroConfirmModal: false,
      showXeroPreviewModal: false,
      showSuccessModal: false,
      selectedEntry: null,
      xeroTestMode: false,
      xeroPreviewData: null,
      previewLoading: false,
      successMessage: '',
      successDetails: []
    };
  },
  computed: {
    baseUrl() {
      if (window.location.pathname.startsWith('/dev')) return '/dev';
      return process.env.VUE_APP_API_URL || '';
    },
    allMonths() {
      return [...this.readyToPush, ...this.alreadyPushed];
    }
  },
  methods: {
    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },
    async fetchAll() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };
        const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig') || 'null');
        if (!selectedPms || !selectedPms.id) {
          this.loading = false;
          return;
        }

        const [xeroRes, financeRes] = await Promise.all([
          axios.get(`${this.baseUrl}/api/pms/xero-status/`, { headers }),
          axios.get(`${this.baseUrl}/api/pms/finance/months-summary/`, {
            headers,
            params: { pms_config_id: selectedPms.id }
          })
        ]);

        if (xeroRes.data.success && xeroRes.data.data.connected) {
          this.isConnected = true;
          this.connectionData = xeroRes.data.data;
        }

        if (financeRes.data.success) {
          this.readyToPush = financeRes.data.data.ready_to_push;
          this.alreadyPushed = financeRes.data.data.already_pushed;
        }
      } catch (error) {
        console.error('Error loading finance data:', error);
      } finally {
        this.loading = false;
      }
    },
    async connectToXero() {
      this.connecting = true;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${this.baseUrl}/api/pms/xero-connect/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.success) {
          window.location.href = response.data.data.authorization_url;
        }
      } catch (error) {
        console.error('Error connecting to Xero:', error);
        alert(error.response?.data?.message || 'Failed to connect to Xero');
      } finally {
        this.connecting = false;
      }
    },
    async disconnectXero() {
      if (!confirm('Are you sure you want to disconnect Xero?')) return;
      this.disconnecting = true;
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${this.baseUrl}/api/pms/xero-disconnect/`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        this.isConnected = false;
        this.connectionData = {};
      } catch (error) {
        console.error('Error disconnecting:', error);
        alert(error.response?.data?.message || 'Failed to disconnect');
      } finally {
        this.disconnecting = false;
      }
    },
    openXeroConfirmModal(entry) {
      if (!entry || !entry.can_push_to_xero) return;
      this.selectedEntry = entry;
      this.xeroTestMode = false;
      this.showXeroConfirmModal = true;
    },
    closeXeroConfirmModal() {
      if (this.pushing || this.previewLoading) return;
      this.showXeroConfirmModal = false;
    },
    closeXeroPreviewModal() {
      this.showXeroPreviewModal = false;
      this.xeroPreviewData = null;
    },
    async confirmXeroAction() {
      if (this.xeroTestMode) {
        await this.fetchXeroPreview();
      } else {
        await this.doPushToXero();
      }
    },
    async fetchXeroPreview() {
      if (!this.selectedEntry) return;
      this.previewLoading = true;
      try {
        const token = localStorage.getItem('token');
        const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig') || 'null');
        if (!selectedPms || !selectedPms.id) {
          this.previewLoading = false;
          alert('No PMS configuration selected.');
          return;
        }
        const response = await fetch(`${this.baseUrl}/api/pms/xero-preview/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            year: this.selectedEntry.year,
            month: this.selectedEntry.month,
            pms_config_id: selectedPms.id
          })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to build preview');
        }

        this.xeroPreviewData = data.data;
        this.showXeroConfirmModal = false;
        this.showXeroPreviewModal = true;
      } catch (error) {
        console.error('Error fetching preview:', error);
        this.successMessage = 'Failed to build Xero preview';
        this.successDetails = [{ type: 'error', text: error.message }];
        this.showXeroConfirmModal = false;
        this.showSuccessModal = true;
      } finally {
        this.previewLoading = false;
      }
    },
    async doPushToXero() {
      if (!this.selectedEntry) return;
      const entry = this.selectedEntry;

      this.showXeroConfirmModal = false;
      this.pushing = true;

      try {
        const token = localStorage.getItem('token');
        const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig') || 'null');
        if (!selectedPms || !selectedPms.id) {
          this.pushing = false;
          alert('No PMS configuration selected.');
          return;
        }
        const response = await fetch(`${this.baseUrl}/api/pms/push-to-xero/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            year: entry.year,
            month: entry.month,
            pms_config_id: selectedPms.id
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to push to Xero');
        }

        if (data.success) {
          const { pushed, failed, results, errors } = data.data;

          this.successMessage = `Successfully pushed ${pushed} invoice${pushed > 1 ? 's' : ''} to Xero`;
          this.successDetails = [];

          if (results && results.length > 0) {
            this.successDetails = results.map(r => ({
              type: 'success',
              text: `${r.property_name}: ${r.invoice_number}`
            }));
          }

          if (failed > 0 && errors && errors.length > 0) {
            errors.forEach(err => {
              this.successDetails.push({
                type: 'error',
                text: `${err.property_name}: ${err.error}`
              });
            });
          }

          this.showSuccessModal = true;
          this.fetchAll();
        } else {
          throw new Error(data.message || 'Unknown error');
        }
      } catch (error) {
        console.error('Error pushing to Xero:', error);
        this.successMessage = 'Failed to push to Xero';
        this.successDetails = [{ type: 'error', text: error.message }];
        this.showSuccessModal = true;
      } finally {
        this.pushing = false;
      }
    },
    toggleBkMonth(label) {
      const idx = this.selectedBkMonths.indexOf(label);
      if (idx >= 0) this.selectedBkMonths.splice(idx, 1);
      else this.selectedBkMonths.push(label);
    },
    async sendToBookkeeper() {
      if (!this.bkEmail) { alert('Please enter a bookkeeper email'); return; }
      if (this.selectedBkMonths.length === 0) { alert('Select at least one month'); return; }

      const monthsByLabel = {};
      this.allMonths.forEach(entry => {
        monthsByLabel[entry.month_label] = { year: entry.year, month: entry.month };
      });
      const months = this.selectedBkMonths
        .map(label => monthsByLabel[label])
        .filter(Boolean);
      if (months.length === 0) {
        alert('Could not resolve selected months. Please refresh and try again.');
        return;
      }

      this.sendingBk = true;
      try {
        const token = localStorage.getItem('token');
        const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig') || 'null');
        if (!selectedPms || !selectedPms.id) {
          alert('No PMS configuration selected.');
          this.sendingBk = false;
          return;
        }
        const response = await axios.post(
          `${this.baseUrl}/api/pms/finance/send-to-bookkeeper/`,
          {
            bookkeeper_email: this.bkEmail,
            cc_email: this.bkEmailCc || null,
            months,
            pms_config_id: selectedPms.id,
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const data = response.data || {};
        if (data.success) {
          alert(
            `Summary sent to ${data.sent_to} `
            + `(${data.count_reports} report${data.count_reports === 1 ? '' : 's'} `
            + `across ${data.count_months} month${data.count_months === 1 ? '' : 's'}).`
          );
          this.showBkPanel = false;
          this.selectedBkMonths = [];
          this.bkEmail = '';
          this.bkEmailCc = '';
        } else {
          alert(data.message || 'Failed to send summary.');
        }
      } catch (error) {
        console.error('Error sending bookkeeper summary:', error);
        const msg = error.response?.data?.message || 'Failed to send summary.';
        alert(msg);
      } finally {
        this.sendingBk = false;
      }
    }
  },
  mounted() {
    this.fetchAll();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600&display=swap');

* { box-sizing: border-box; margin: 0; padding: 0; }

.main { margin-left: 240px; flex: 1; display: flex; flex-direction: column; min-height: 100vh; background: #f5f4fb; font-family: 'Inter', sans-serif; }
.page-content { padding: 24px 28px; }

.card { background: #ffffff; border-radius: 10px; border: 1px solid #e8e6f4; box-shadow: 0 1px 4px rgba(26,24,61,0.07); }
.card-pad { padding: 20px 22px; }

.section-label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #9896bb; margin-bottom: 10px; }

/* Xero connected */
.xero-connected { background: #ffffff; border: 1.5px solid rgba(0,184,148,0.25); border-radius: 10px; padding: 15px 18px; display: flex; align-items: center; gap: 12px; margin-bottom: 20px; box-shadow: 0 1px 4px rgba(26,24,61,0.07); }
.xero-icon { width: 34px; height: 34px; background: rgba(0,184,148,0.1); border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #00b894; }
.xero-info { flex: 1; }
.xero-title { font-size: 14px; font-weight: 600; color: #00b894; }
.xero-sub { font-size: 13px; color: #9896bb; margin-top: 1px; }

/* Push cards */
.push-card { background: #ffffff; border: 1px solid #e8e6f4; border-radius: 10px; padding: 18px 20px; display: flex; align-items: center; gap: 14px; box-shadow: 0 1px 4px rgba(26,24,61,0.07); margin-bottom: 10px; }
.push-card-icon { width: 38px; height: 38px; background: rgba(91,79,207,0.1); border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.push-card-icon svg { width: 18px; height: 18px; fill: none; stroke-width: 2; }
.push-card-info { flex: 1; }
.push-card-title { font-size: 15px; font-weight: 600; color: #1a1a2e; }
.push-card-sub { font-size: 13px; color: #9896bb; margin-top: 2px; }

/* Table */
.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; }
thead th { padding: 12px 16px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #9896bb; border-bottom: 1px solid #e8e6f4; text-align: left; white-space: nowrap; }
tbody td { padding: 14px 16px; font-size: 14px; border-bottom: 1px solid #f0eef8; vertical-align: middle; background: #ffffff; }
tbody tr:last-child td { border-bottom: none; }
tbody tr:hover td { background: #faf9ff; }
.prop-name { font-weight: 600; color: #1a1a2e; font-size: 14px; }
.amount { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 14px; }

/* Check circle */
.check-circle { width: 26px; height: 26px; border-radius: 50%; background: rgba(0,184,148,0.1); border: 1.5px solid rgba(0,184,148,0.25); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.check-circle svg { width: 12px; height: 12px; fill: none; stroke: #00b894; stroke-width: 2.5; }

/* Buttons */
.btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 7px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; font-family: 'Inter', sans-serif; transition: opacity 0.15s, transform 0.1s; white-space: nowrap; }
.btn:hover { opacity: 0.88; }
.btn-primary { background: #5b4fcf; color: #fff; }
.btn-outline { background: transparent; color: #4a4880; border: 1.5px solid #e8e6f4; }
.btn-ghost { background: #f5f4fb; color: #4a4880; }
.btn-xero { background: #13B5EA; color: #fff; }
.btn-sm { padding: 6px 13px; font-size: 13px; }

/* Bookkeeper month cards */
.bk-month-card { border: 1.5px solid #e8e6f4; border-radius: 8px; padding: 10px 12px; cursor: pointer; position: relative; background: #ffffff; transition: border-color 0.15s, background 0.15s; user-select: none; }
.bk-month-card:hover { border-color: #7c6fdf; background: rgba(91,79,207,0.1); }
.bk-month-card.selected { border-color: #5b4fcf; background: rgba(91,79,207,0.1); }
.bk-tick { position: absolute; top: 8px; right: 10px; font-size: 11px; font-weight: 700; color: #5b4fcf; }

/* Email row */
.email-row { display: flex; gap: 8px; align-items: center; margin-bottom: 8px; }
.email-row label { font-size: 13px; color: #9896bb; width: 90px; flex-shrink: 0; }
.email-input { flex: 1; border: 1.5px solid #e8e6f4; border-radius: 7px; padding: 8px 12px; font-size: 14px; color: #1a1a2e; font-family: 'Inter', sans-serif; background: #ffffff; outline: none; }
.email-input:focus { border-color: #5b4fcf; }

/* Spinner */
.spinner { width: 32px; height: 32px; border: 3px solid #e8e6f4; border-top-color: #5b4fcf; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ============= MODALS (mirror MonthEndView pattern) ============= */
.modal-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; padding: 20px;
}
.modal-send-report {
  background: white; border-radius: 16px; width: 100%; max-width: 440px;
  overflow: hidden; box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}
.modal-header-gradient {
  background: linear-gradient(135deg, #8075f0 0%, #9b8ff5 100%);
  padding: 28px 24px; text-align: center; color: white;
}
.modal-icon-circle {
  width: 52px; height: 52px; background: rgba(255, 255, 255, 0.2);
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  margin: 0 auto 14px;
}
.modal-icon-circle svg { width: 20px; height: 20px; }
.modal-title {
  font-family: 'Outfit', 'Bricolage Grotesque', sans-serif;
  font-size: 19px; font-weight: 700; margin: 0 0 6px 0;
}
.modal-subtitle { font-size: 13px; margin: 0; opacity: 0.9; font-weight: 500; }
.modal-body { padding: 24px; }
.warning-message {
  display: flex; align-items: center; gap: 10px; padding: 12px;
  background: #fffbeb; border: 1.5px solid #fde68a; border-radius: 10px;
  font-size: 13px; color: #92400e; font-weight: 500;
}
.warning-message svg { flex-shrink: 0; width: 18px; height: 18px; }
.modal-footer {
  padding: 18px 24px; border-top: 1px solid #f3f4f6;
  display: flex; gap: 10px; justify-content: flex-end;
}
.btn-modal-cancel {
  padding: 10px 20px; border: 1.5px solid #e5e7eb; background: white;
  border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;
  color: #4b5563; transition: all 0.2s; font-family: 'Inter', 'DM Sans', sans-serif;
}
.btn-modal-cancel:hover { border-color: #d1d5db; background: #f9fafb; }
.btn-modal-cancel:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-modal-send {
  padding: 10px 24px; border: none; background: #8075f0; color: white;
  border-radius: 8px; font-size: 14px; font-weight: 700; cursor: pointer;
  transition: all 0.2s; font-family: 'Inter', 'DM Sans', sans-serif;
}
.btn-modal-send:hover:not(:disabled) { background: #6f63e6; transform: translateY(-1px); }
.btn-modal-send:disabled { opacity: 0.6; cursor: not-allowed; }

/* Test mode toggle */
.xero-test-mode-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; margin-top: 16px; padding: 14px 16px;
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;
}
.xero-test-mode-info { flex: 1; }
.xero-test-mode-title { font-size: 14px; font-weight: 600; color: #1f2937; }
.xero-test-mode-help {
  font-size: 12px; color: #6b7280; margin: 4px 0 0 0;
  font-style: italic; line-height: 1.4;
}
.toggle-switch {
  position: relative; display: inline-block;
  width: 48px; height: 24px; flex-shrink: 0;
}
.toggle-switch input[type="checkbox"] { opacity: 0; width: 0; height: 0; }
.toggle-slider {
  position: absolute; cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #cbd5e0; transition: 0.3s; border-radius: 24px;
}
.toggle-slider:before {
  position: absolute; content: "";
  height: 18px; width: 18px; left: 3px; bottom: 3px;
  background-color: white; transition: 0.3s; border-radius: 50%;
}
.toggle-switch input:checked + .toggle-slider { background-color: #667eea; }
.toggle-switch input:checked + .toggle-slider:before { transform: translateX(24px); }
.toggle-switch input:focus + .toggle-slider { box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2); }
.toggle-switch input:disabled + .toggle-slider { opacity: 0.5; cursor: not-allowed; }

/* Preview modal */
.modal-xero-preview {
  background: white; border-radius: 16px;
  max-width: 1100px; width: 95%; max-height: 90vh;
  display: flex; flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); overflow: hidden;
}
.modal-body-scroll { overflow-y: auto; flex: 1; }
.xero-preview-invoice {
  border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 16px; margin-bottom: 16px; background: #fafbfc;
}
.xero-preview-invoice:last-child { margin-bottom: 0; }
.xero-preview-invoice-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #e5e7eb;
}
.xero-preview-invoice-title { font-size: 16px; font-weight: 700; color: #111827; }
.xero-preview-invoice-period { font-size: 13px; color: #6b7280; font-weight: 500; }
.xero-preview-meta-grid {
  display: grid; grid-template-columns: 1fr; gap: 6px; margin-bottom: 14px;
}
.xero-preview-meta-row { display: flex; align-items: center; gap: 8px; font-size: 13px; }
.xero-preview-meta-label { color: #6b7280; font-weight: 500; min-width: 110px; }
.xero-preview-meta-value {
  color: #111827; font-weight: 500;
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
}
.xero-preview-meta-sub { color: #6b7280; font-weight: 400; }
.xero-preview-badge-new {
  background: #fef3c7; color: #92400e;
  padding: 2px 8px; border-radius: 6px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;
}
.xero-preview-table {
  width: 100%; border-collapse: collapse; background: white;
  border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; font-size: 13px;
}
.xero-preview-table th {
  background: #f3f4f6; text-align: left; padding: 10px 12px;
  font-weight: 600; color: #374151; border-bottom: 1px solid #e5e7eb;
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.03em;
}
.xero-preview-table th.num, .xero-preview-table td.num { text-align: right; }
.xero-preview-table td { padding: 10px 12px; border-bottom: 1px solid #f3f4f6; color: #111827; }
.xero-preview-table tbody tr:last-child td { border-bottom: 1px solid #e5e7eb; }
.xero-preview-table tfoot td {
  background: #f9fafb; padding: 8px 12px; font-size: 13px;
  border-bottom: 1px solid #f3f4f6;
}
.xero-preview-total-label { text-align: right; font-weight: 600; color: #374151; }
.xero-preview-table tfoot tr.xero-preview-grand-total td {
  background: #eef2ff; font-weight: 700; color: #1e3a8a;
  font-size: 14px; border-bottom: none;
}
.xero-preview-novat-badge {
  display: inline-block; padding: 2px 8px;
  background: #f3f4f6; color: #6b7280; border-radius: 6px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;
}
.xero-preview-row-novat td { background: #fafafa; }

/* Success modal */
.modal-success {
  background: white; border-radius: 16px;
  max-width: 520px; width: 90%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}
.modal-header-success {
  padding: 40px 32px 24px; text-align: center; border-bottom: 1px solid #f3f4f6;
}
.checkmark-circle { width: 80px; height: 80px; margin: 0 auto 20px; position: relative; }
.checkmark {
  width: 80px; height: 80px; border-radius: 50%; display: block;
  stroke-width: 3; stroke: #10b981; stroke-miterlimit: 10;
  box-shadow: inset 0px 0px 0px #10b981;
  animation: fill 0.4s ease-in-out 0.4s forwards, scaleCheck 0.3s ease-in-out 0.9s both;
}
.checkmark-circle-bg {
  stroke-dasharray: 166; stroke-dashoffset: 166;
  stroke-width: 3; stroke-miterlimit: 10; stroke: #10b981; fill: none;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}
.checkmark-check {
  transform-origin: 50% 50%;
  stroke-dasharray: 48; stroke-dashoffset: 48;
  stroke: #10b981; stroke-width: 3;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}
@keyframes stroke { 100% { stroke-dashoffset: 0; } }
@keyframes scaleCheck { 0%, 100% { transform: none; } 50% { transform: scale3d(1.1, 1.1, 1); } }
@keyframes fill { 100% { box-shadow: inset 0px 0px 0px 30px #10b981; } }
.modal-title-success {
  font-size: 22px; font-weight: 700; color: #1f2937;
  margin: 0; font-family: 'Outfit', 'DM Sans', sans-serif;
}
.modal-body-success { padding: 24px 32px; max-height: 400px; overflow-y: auto; }
.success-details { display: flex; flex-direction: column; gap: 12px; }
.detail-item {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 12px 16px; border-radius: 8px; background: #f9fafb; font-size: 14px;
}
.detail-success { background: #ecfdf5; border-left: 3px solid #10b981; }
.detail-error { background: #fef2f2; border-left: 3px solid #ef4444; }
.detail-icon { font-weight: bold; font-size: 16px; flex-shrink: 0; }
.detail-success .detail-icon { color: #10b981; }
.detail-error .detail-icon { color: #ef4444; }
.detail-text { color: #4b5563; line-height: 1.5; font-family: 'Inter', 'DM Sans', sans-serif; }
.modal-footer-success {
  padding: 20px 32px; border-top: 1px solid #f3f4f6;
  display: flex; justify-content: center;
}
.btn-modal-ok {
  padding: 12px 32px; border: none; background: #8075f0; color: white;
  border-radius: 8px; font-size: 14px; font-weight: 700; cursor: pointer;
  transition: all 0.2s; font-family: 'Inter', 'DM Sans', sans-serif; min-width: 120px;
}
.btn-modal-ok:hover {
  background: #6d61e0; transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(128, 117, 240, 0.3);
}
</style>
